import React, { useState } from "react";
import toast from "react-hot-toast";

import {
  DialogProps,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Button,
  Spinner,
  Input,
  Switch,
  Field,
  Divider,
  InfoLabel,
} from "@fluentui/react-components";

import { Stack } from "../stack";

import { useAuth } from "@/context/auth";
import { useDocumentClassification } from "@/context/document-classification";

import { Playbook } from "@/types/risk-analysis";
import log from "@/utils/logger";

export interface DuplicatePlaybookDialogProps extends Omit<DialogProps, "children"> {
  playbook: Playbook;
}

export function DuplicatePlaybookDialog({ playbook, onOpenChange, ...props }: DuplicatePlaybookDialogProps) {
  const { user: auth } = useAuth();
  const { copyPlaybook } = useDocumentClassification();

  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>(`${playbook.name} (copy)`);
  const [isEditable, setIsEditable] = useState<boolean>(playbook.editable);
  const [isPrivate, setIsPrivate] = useState<boolean>(!!playbook.ownerUserId && playbook.ownerUserId === auth.user.id);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      await copyPlaybook(playbook.id, name, isPrivate, isEditable, !playbook.customerId ? playbook.id : undefined);
      onOpenChange?.(undefined, {
        open: false,
        type: "triggerClick",
        event: undefined,
      });
    } catch (error) {
      toast.error(
        error?.message || "Something went wrong while trying to duplicate the playbook. Please try again later."
      );
      log.error("failed to duplicate playbook", "DuplicatePlaybookDialog", { playbook });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} {...props}>
      <DialogSurface
        aria-labelledby={`duplicate-${playbook.id}-title`}
        aria-describedby={`duplicate-${playbook.id}-content`}
      >
        <form onSubmit={handleSubmit}>
          <DialogBody>
            <DialogTitle id={`duplicate-${playbook.id}-title`}>Duplicate Playbook</DialogTitle>
            <DialogContent id={`duplicate-${playbook.id}-content`}>
              <Field required label="Playbook Name">
                <Input required value={name} id="playbook-name" onChange={(e) => setName(e.target.value)} />
              </Field>
              <Stack align="center" justify="end" gap="0.5rem" style={{ marginTop: "0.5rem" }}>
                <Switch
                  label="Private"
                  labelPosition="before"
                  disabled={loading}
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                />
                <Divider vertical style={{ flexGrow: 0 }} />
                <InfoLabel
                  info="If enabled, any user with access to this playbook can make changes. If disabled, only you, the creator of this playbook, can make changes."
                  style={{ display: "inline-flex", flexDirection: "row", alignItems: "center" }}
                >
                  <Switch
                    label="Editable"
                    labelPosition="before"
                    disabled={loading}
                    checked={isEditable}
                    onChange={(e) => setIsEditable(e.target.checked)}
                  />
                </InfoLabel>
              </Stack>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button disabled={loading} appearance="secondary">
                  Cancel
                </Button>
              </DialogTrigger>
              <Button
                type="submit"
                disabled={loading}
                appearance="primary"
                icon={loading ? <Spinner size="tiny" /> : null}
              >
                Duplicate
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}
